import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FiArrowRight, FiAward, FiBriefcase, FiList } from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { useDadosVivos } from '../../hooks/useDadosVivos';
import {
  pssPortalService, STATUS_INSCRICAO_LABEL, STATUS_PSS_LABEL,
  type MinhaInscricao,
} from '../../services/pss.service';

export function PssMinhasInscricoesPage() {
  const { dados, carregando } = useDadosVivos<MinhaInscricao[]>(
    () => pssPortalService.minhasInscricoes(),
    [],
  );
  const inscricoes = dados ?? [];

  return (
    <PortalPage
      titulo="Minhas inscrições"
      subtitulo="Processos seletivos que você se inscreveu."
      icone={<FiList size={26} />}
      voltarHref="/"
      largura="larga"
    >
      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : inscricoes.length === 0 ? (
        <SectionCard>
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <FiBriefcase className="text-slate-400" size={26} />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-4">Você ainda não se inscreveu em nenhum processo seletivo.</p>
            <Link to="/pss" className="text-sm text-institutional hover:underline font-medium">
              Ver editais disponíveis
            </Link>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-3">
          {inscricoes.map((i) => (
            <SectionCard key={i.id}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <FiAward size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs font-medium text-slate-500 tabular-nums">{i.edital.numero}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      i.status_inscricao === 'aprovada' ? 'bg-emerald-100 text-emerald-700' :
                      i.status_inscricao === 'reprovada' ? 'bg-amber-100 text-amber-700' :
                      i.status_inscricao === 'desclassificada' ? 'bg-red-100 text-red-700' :
                      i.status_inscricao === 'classificada' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {STATUS_INSCRICAO_LABEL[i.status_inscricao]}
                    </span>
                    {i.pontuacao != null && (
                      <span className="text-xs text-slate-500">{i.pontuacao} pts</span>
                    )}
                  </div>
                  <div className="text-base font-medium text-slate-900 mt-1 truncate">{i.edital.titulo}</div>
                  {i.edital.cargo && <div className="text-sm text-slate-600 mt-0.5">{i.edital.cargo}</div>}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>Inscrição {i.numero}</span>
                    <span>· Inscrito em {format(parseISO(i.data_abertura), "dd/MM/yyyy", { locale: ptBR })}</span>
                    <span>· Edital {STATUS_PSS_LABEL[i.edital.status_pss]}</span>
                  </div>
                  {i.motivo_decisao && (
                    <div className="mt-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-2">
                      <strong>Decisão:</strong> {i.motivo_decisao}
                    </div>
                  )}
                </div>
                <Link
                  to={`/pss/${i.edital.numero}`}
                  className="text-slate-400 hover:text-institutional"
                >
                  <FiArrowRight size={16} />
                </Link>
              </div>
            </SectionCard>
          ))}
        </div>
      )}
    </PortalPage>
  );
}
